import React, { useEffect, useMemo, useReducer } from "react";
import { Button, Input, message, Tooltip } from "antd";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { useReactiveVar } from "@apollo/client";

import useEffectiveLanguage from "components/use-hooks/use-effective-language";
import { localizeIt } from "components/service/localize";
import TranslateLanguageIcon from "components/layout/icons/translate-language-icon";
import { translationParamsVar } from "graphql/cache";

import { hasTranslationApiAccess } from "../translation-ai-client";
import { requestInputTranslation } from "../dynamic-translation";
import { detectGermanEnglishTextLanguage } from "../translation-language-detection";

import {
    getOptionalTranslationDirection,
    getOptionalViewMode,
    getReverseTranslationDirection,
    isOptionalTranslationLanguage,
    shouldRetryOptionalTranslation,
    TARGET_LANGUAGE
} from "./translation-direction";
import {
    getTextLanguageHint,
    setTextLanguageHint
} from "./translation-text-hints";
import {
    createTextareaTranslationState,
    getInitialTextareaTranslationState,
    getTextareaTranslationSubmitState,
    getTextValue,
    textareaTranslationReducer
} from "./translation-state";

import "./translated-textarea-control.scss";

const TranslatedTextareaControl = React.forwardRef(({
        value,
        onChange,
        onTranslationStateChange,
        initialTranslatedValue = "",
        namespace = "input",
        context = "",
        className = "",
        resetKey = "",
        ...props
    }, ref) => {
    const { language } = useEffectiveLanguage();
    const translationParams = useReactiveVar(translationParamsVar);
    const optionalTranslate = isOptionalTranslationLanguage(language);
    const strictTranslate = !!language && !optionalTranslate;
    const canTranslate = (strictTranslate || optionalTranslate) && hasTranslationApiAccess(translationParams);
    const visibleValue = optionalTranslate ?
        getTextValue(value) :
        (getTextValue(initialTranslatedValue) || getTextValue(value));
    const languageHint = optionalTranslate ?
        getTextLanguageHint({ namespace, context, text: visibleValue }) :
        null;
    const initialPayload = {
        value,
        initialTranslatedValue,
        canTranslate,
        strictTranslate,
        language,
        languageHint
    };
    const [ state, dispatch ] = useReducer(
        textareaTranslationReducer,
        initialPayload,
        createTextareaTranslationState
    );

    const translationState = useMemo(() => getTextareaTranslationSubmitState({
        state,
        canTranslate,
        strictTranslate,
        language
    }), [ state, canTranslate, strictTranslate, language ]);

    const setInputValue = (nextValue) => {
        if (!onChange) return;

        onChange({
            target: {
                value: nextValue
            }
        });
    };

    useEffect(() => {
        dispatch({
            type    : "reset",
            payload : {
                value,
                initialTranslatedValue,
                canTranslate,
                strictTranslate,
                language,
                languageHint
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ resetKey, initialTranslatedValue, canTranslate, language, languageHint ]);

    useEffect(() => {
        if (!onTranslationStateChange) return;

        onTranslationStateChange(translationState);
    }, [ onTranslationStateChange, translationState ]);

    const handleChange = (e) => {
        const nextText = getTextValue(e?.target?.value);

        dispatch({
            type: "change",
            text: nextText,
            canTranslate,
            strictTranslate,
            language
        });

        setInputValue(nextText);
    };

    const translateOptionalText = async (currentText) => {
        const textLanguage = getTextLanguageHint({ namespace, context, text: currentText }) ||
            detectGermanEnglishTextLanguage(currentText);
        const direction = getOptionalTranslationDirection({
            language,
            viewMode: state.viewMode,
            textLanguage
        });

        let activeDirection = direction;
        let translatedText;

        if (activeDirection.sourceLang === activeDirection.targetLang) {
            throw new Error("Optional translation direction is invalid");
        }

        try {
            translatedText = await requestInputTranslation({
                sourceLang : activeDirection.sourceLang,
                targetLang : activeDirection.targetLang,
                namespace,
                context,
                text       : currentText
            });
        } catch (error) {
            if (!shouldRetryOptionalTranslation(error)) throw error;

            activeDirection = getReverseTranslationDirection(activeDirection);
            translatedText = await requestInputTranslation({
                sourceLang : activeDirection.sourceLang,
                targetLang : activeDirection.targetLang,
                namespace,
                context,
                text       : currentText
            });
        }

        if (getTextValue(translatedText) === currentText) {
            throw new Error("Optional translation returned unchanged text");
        }

        setTextLanguageHint({
            namespace,
            context,
            text     : currentText,
            language : activeDirection.sourceLang
        });
        setTextLanguageHint({
            namespace,
            context,
            text     : translatedText,
            language : activeDirection.targetLang
        });

        dispatch({
            type     : "translated_optional",
            text     : translatedText,
            viewMode : getOptionalViewMode(language, activeDirection.targetLang)
        });
        setInputValue(translatedText);
    };

    const translateStrictText = async (currentText) => {
        if (state.viewMode === "de" && state.deDirty) {
            const translatedText = await requestInputTranslation({
                sourceLang : language,
                targetLang : TARGET_LANGUAGE,
                namespace,
                context,
                text       : currentText
            });

            dispatch({
                type       : "translated_to_de",
                deText     : translatedText,
                nativeText : null
            });
            setInputValue(translatedText);
            return;
        }

        if (state.viewMode === "de" && state.nativeText !== null) {
            dispatch({ type: "show_native" });
            setInputValue(state.nativeText || "");
            return;
        }

        if (state.viewMode === "native" && !state.nativeDirty) {
            dispatch({ type: "show_de" });
            setInputValue(state.deText);
            return;
        }

        if (state.viewMode === "native") {
            const translatedText = await requestInputTranslation({
                sourceLang : language,
                targetLang : TARGET_LANGUAGE,
                namespace,
                context,
                text       : currentText
            });

            dispatch({
                type       : "translated_to_de",
                deText     : translatedText,
                nativeText : currentText
            });
            setInputValue(translatedText);
            return;
        }

        const translatedText = await requestInputTranslation({
            sourceLang : TARGET_LANGUAGE,
            targetLang : language,
            namespace,
            context,
            text       : currentText
        });

        dispatch({
            type       : "translated_to_native",
            deText     : currentText,
            nativeText : translatedText
        });
        setInputValue(translatedText);
    };

    const handleTranslate = async () => {
        const currentText = getTextValue(state.visibleText);

        if (!canTranslate || !currentText.trim() || state.status === "loading") return;

        dispatch({ type: "loading" });

        try {
            if (strictTranslate) {
                await translateStrictText(currentText);
            } else {
                await translateOptionalText(currentText);
            }
        } catch (e) {
            dispatch({ type: "error" });
            message.error(localizeIt("TRANSLATION.Text_InputTranslationError"));
        }
    };

    const tooltipText = !strictTranslate ?
        localizeIt("TRANSLATION.Button_Text_Translate") :
        (state.viewMode === "native" ?
            (state.nativeDirty ?
                localizeIt("TRANSLATION.Button_Text_TranslateInput") :
                localizeIt("TRANSLATION.Button_Text_ShowOriginal")
            ) :
            (state.deDirty ?
                localizeIt("TRANSLATION.Button_Text_TranslateInput") :
                localizeIt("TRANSLATION.Button_Text_TranslateNative")
            )
        );

    return (
        <div className={ `translated-textarea-control ${ canTranslate ? "with-translation" : "" } ${ className }` }>
            <Input.TextArea
                { ...props }
                ref={ ref }
                value={ state.visibleText }
                disabled={ props.disabled || state.status === "loading" }
                onChange={ handleChange }
            />
            { canTranslate &&
                <Tooltip title={ tooltipText }>
                    <span className={ `translated-textarea-button-wrap status-${ state.status }` }>
                        <Button
                            type="text"
                            className={ `translated-textarea-button ${ state.status === "loading" ? "is-loading" : "" }` }
                            disabled={ !state.visibleText.trim() }
                            onClick={ handleTranslate }
                            aria-label={ tooltipText }
                            aria-busy={ state.status === "loading" }
                            aria-disabled={ state.status === "loading" }
                        >
                            { state.status === "loading" ? <LoadingOutlined spin /> : <TranslateLanguageIcon /> }
                        </Button>
                    </span>
                </Tooltip>
            }
            { canTranslate && state.status !== "idle" &&
                <div className={ `translated-textarea-status ${ state.status }` }>
                    { state.status === "translated" &&
                        <>
                            <CheckOutlined />
                            <span>{ localizeIt("TRANSLATION.Text_InputTranslated") }</span>
                        </>
                    }
                    { state.status === "loading" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslating") }</span>
                    }
                    { state.status === "needs_translation" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslationRequired") }</span>
                    }
                    { state.status === "error" &&
                        <span>{ localizeIt("TRANSLATION.Text_InputTranslationError") }</span>
                    }
                </div>
            }
        </div>
    );
});

TranslatedTextareaControl.defaultProps = {
    onTranslationStateChange: null
};

export {
    getInitialTextareaTranslationState
};

export default TranslatedTextareaControl;
